export const ACCOUNTING_MAPPING_SCHEMA = "coetic-x.invoice-studio.accounting-mapping";
export const ACCOUNTING_MAPPING_VERSION = 1;

export const ACCOUNTING_MAPPING_STATUSES = Object.freeze([
  { id: "needs-review", label: "Needs review" },
  { id: "confirmed", label: "Confirmed" },
  { id: "retired", label: "Retired" },
]);

const compact = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
const normalized = (value) => compact(value).normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const list = (value) => Array.isArray(value) ? value.map(compact).filter(Boolean) : compact(value) ? [compact(value)] : [];
const idPart = (value) => normalized(value).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 56) || "mapping";

export const normalizeAccountingMapping = (rule = {}) => ({
  schema: ACCOUNTING_MAPPING_SCHEMA,
  version: ACCOUNTING_MAPPING_VERSION,
  id: compact(rule.id) || `accounting-map-${idPart(rule.name || rule.category)}`,
  workspaceId: compact(rule.workspaceId),
  businessId: compact(rule.businessId),
  name: compact(rule.name) || "Accounting rule",
  direction: ["sale", "purchase", "ledger"].includes(rule.direction) ? rule.direction : "sale",
  sourceType: compact(rule.sourceType) || "invoice",
  match: {
    partyName: compact(rule.match?.partyName),
    serviceName: compact(rule.match?.serviceName),
    descriptionContains: list(rule.match?.descriptionContains),
    quickBooksAccount: compact(rule.match?.quickBooksAccount),
  },
  category: compact(rule.category) || "unclassified",
  quickBooks: {
    itemName: compact(rule.quickBooks?.itemName),
    accountPath: compact(rule.quickBooks?.accountPath),
    receivableAccountPath: compact(rule.quickBooks?.receivableAccountPath),
    taxCategory: compact(rule.quickBooks?.taxCategory),
  },
  edi: {
    profile: compact(rule.edi?.profile),
    treatmentId: compact(rule.edi?.treatmentId),
    identifier: compact(rule.edi?.identifier),
    boxKeys: list(rule.edi?.boxKeys),
  },
  declarations: {
    cdCategory: compact(rule.declarations?.cdCategory),
    tmQlCategory: compact(rule.declarations?.tmQlCategory),
  },
  status: ACCOUNTING_MAPPING_STATUSES.some((status) => status.id === rule.status) ? rule.status : "needs-review",
  confidence: Number.isFinite(Number(rule.confidence)) ? Math.max(0, Math.min(1, Number(rule.confidence))) : 0,
  effectiveFrom: compact(rule.effectiveFrom),
  effectiveTo: compact(rule.effectiveTo),
  reviewedBy: compact(rule.reviewedBy),
  reviewedAt: compact(rule.reviewedAt),
  reviewNote: compact(rule.reviewNote),
  revision: Number.isInteger(Number(rule.revision)) && Number(rule.revision) > 0 ? Number(rule.revision) : undefined,
  createdAt: compact(rule.createdAt),
  updatedAt: compact(rule.updatedAt),
  evidence: Array.isArray(rule.evidence) ? rule.evidence.map((entry) => ({
    sourceId: compact(entry?.sourceId),
    sheet: compact(entry?.sheet),
    range: compact(entry?.range),
    trust: compact(entry?.trust),
  })).filter((entry) => entry.sourceId || entry.sheet || entry.range) : [],
});

export const validateAccountingMapping = (candidate) => {
  const rule = normalizeAccountingMapping(candidate);
  const errors = [];
  const warnings = [];
  const hasMatcher = rule.match.partyName || rule.match.serviceName || rule.match.descriptionContains.length || rule.match.quickBooksAccount;
  if (!rule.businessId) errors.push("Choose an issuing business.");
  if (!hasMatcher) errors.push("Add at least one source signal so the rule cannot match every record.");
  if (!rule.category || rule.category === "unclassified") errors.push("Choose a normalized accounting category.");
  if (rule.direction === "sale" && (!rule.quickBooks.itemName || !rule.quickBooks.accountPath)) {
    warnings.push("Sales exports still need a QuickBooks item and income account.");
  }
  if (["sale", "purchase"].includes(rule.direction) && !rule.edi.treatmentId) {
    warnings.push("No EDI treatment is attached to this rule.");
  }
  if (rule.status === "confirmed" && errors.length) errors.push("A rule with structural errors cannot be confirmed.");
  return { rule, errors: [...new Set(errors)], warnings: [...new Set(warnings)], valid: errors.length === 0 };
};

const includesAll = (haystack, needles) => needles.every((needle) => normalized(haystack).includes(normalized(needle)));
const inEffectiveRange = (rule, date) => {
  const candidate = compact(date);
  if (!candidate) return true;
  if (rule.effectiveFrom && candidate < rule.effectiveFrom) return false;
  if (rule.effectiveTo && candidate > rule.effectiveTo) return false;
  return true;
};

export const scoreAccountingMapping = (candidate, context = {}) => {
  const rule = normalizeAccountingMapping(candidate);
  if (rule.status === "retired") return -1;
  if (context.businessId && rule.businessId && context.businessId !== rule.businessId) return -1;
  if (context.direction && rule.direction !== context.direction) return -1;
  if (rule.sourceType && context.sourceType && rule.sourceType !== context.sourceType && rule.sourceType !== "invoice") return -1;
  if (!inEffectiveRange(rule, context.date)) return -1;

  let score = 0;
  let matchers = 0;
  if (rule.match.partyName) {
    matchers += 1;
    if (normalized(rule.match.partyName) !== normalized(context.partyName)) return -1;
    score += 32;
  }
  if (rule.match.serviceName) {
    matchers += 1;
    if (normalized(rule.match.serviceName) !== normalized(context.serviceName)) return -1;
    score += 28;
  }
  if (rule.match.quickBooksAccount) {
    matchers += 1;
    if (normalized(rule.match.quickBooksAccount) !== normalized(context.quickBooksAccount)) return -1;
    score += 36;
  }
  if (rule.match.descriptionContains.length) {
    matchers += 1;
    if (!includesAll(context.description, rule.match.descriptionContains)) return -1;
    score += 12 + Math.min(12, rule.match.descriptionContains.length * 3);
  }
  if (!matchers) return -1;
  if (rule.sourceType === context.sourceType) score += 4;
  if (rule.status === "confirmed") score += 100;
  return score;
};

export const findAccountingMapping = (rules = [], context = {}) => {
  const matches = rules
    .map((candidate) => ({ rule: normalizeAccountingMapping(candidate), score: scoreAccountingMapping(candidate, context) }))
    .filter((entry) => entry.score >= 0)
    .sort((left, right) => right.score - left.score || right.rule.confidence - left.rule.confidence || left.rule.name.localeCompare(right.rule.name));
  return { rule: matches[0]?.rule || null, score: matches[0]?.score ?? -1, alternatives: matches.slice(1) };
};

export const mergeAccountingMappings = (...collections) => {
  const merged = new Map();
  collections.flat().filter(Boolean).forEach((candidate) => {
    const rule = normalizeAccountingMapping(candidate);
    const existing = merged.get(rule.id);
    if (!existing) {
      merged.set(rule.id, rule);
      return;
    }
    merged.set(rule.id, {
      ...existing,
      ...rule,
      evidence: [...new Map([...existing.evidence, ...rule.evidence].map((entry) => [`${entry.sourceId}:${entry.sheet}:${entry.range}`, entry])).values()],
    });
  });
  return [...merged.values()];
};

export const accountingMappingMatchLabel = (candidate) => {
  const rule = normalizeAccountingMapping(candidate);
  if (rule.match.partyName) return rule.match.partyName;
  if (rule.match.serviceName) return rule.match.serviceName;
  if (rule.match.quickBooksAccount) return rule.match.quickBooksAccount;
  if (rule.match.descriptionContains.length) return rule.match.descriptionContains.join(" + ");
  return "No source signal";
};
